import { useEffect } from 'react';
import { observer } from 'mobx-react';

import { useDependencies } from '@servicetitan/react-ioc';
import { TextField, Switch } from '@servicetitan/anvil2';

import { useFormInputs } from '../../hooks/use-form-inputs';
import { LoadStatus } from '../../../common/enums/load-status';
import { ManagePartnerStore } from '../../stores/manage-partner.store';
import { NotificationsStore } from '../../../common/stores/notifications.store';
import { ConfigurationWrapper } from '../configuration-helpers/configuration-wrapper';
import { ActiveFieldsSection } from '../configuration-helpers/active-fields-section';
import { TitleField } from '../configuration-helpers/title-field';
import { OptInOutField } from '../configuration-helpers/opt-in-out-field';
import { SectionsDivider } from '../manage-partition/sections-divider';
import { DomainsListField } from '../configuration-helpers/domains-list-field';
import { DisabledFieldsSection } from '../configuration-helpers/disabled-fields-section';

export const PartnerConfiguration = observer(() => {
    const [
        { formState, partner, partnerGenericConfigs, saveLoadStatus, resetConfiguration },
        { notifyInSnackbar },
    ] = useDependencies(ManagePartnerStore, NotificationsStore);

    const { getInputProps } = useFormInputs();

    const {
        emailScope,
        emailSenderDomain,
        name,
        directMailProvider,
        authPath,
        partnerKBUrl,
        emailTemplateOptOut,
        supportCallForwarding,
        managedDomains,
    } = formState.$;

    useEffect(() => {
        if (saveLoadStatus === LoadStatus.Ok) {
            notifyInSnackbar({ title: 'Changes saved' });
        }
    }, [saveLoadStatus, notifyInSnackbar]);

    useEffect(
        () => () => {
            resetConfiguration();
        },
        [resetConfiguration],
    );

    return (
        <ConfigurationWrapper isLoaded={!!partner}>
            <ActiveFieldsSection>
                <TitleField title={partner?.displayName} />
                <TextField label="Partner Name" {...getInputProps(name)} />
                <TextField label="Email Sender Domain" {...getInputProps(emailSenderDomain)} />
                <DomainsListField
                    key={partner?.internalId}
                    label="Managed Domains"
                    value={managedDomains.value}
                    onChange={managedDomains.onChange}
                />
                <TextField label="Email Scope" {...getInputProps(emailScope)} />
                <TextField label="Direct Mail Provider" {...getInputProps(directMailProvider)} />
                {partner?.configs.isPartnerLevelAuth && (
                    <TextField label="Auth Path URL" {...getInputProps(authPath)} />
                )}
                <TextField label="Knowledge Base URL" {...getInputProps(partnerKBUrl)} />
                <OptInOutField field={emailTemplateOptOut} />
                <Switch
                    label="Support Call Forwarding"
                    checked={supportCallForwarding.value}
                    onChange={(_, { checked }) => {
                        supportCallForwarding.onChange(checked);
                    }}
                />
            </ActiveFieldsSection>
            <SectionsDivider showDivider={partnerGenericConfigs.size > 0} />
            <DisabledFieldsSection configs={partnerGenericConfigs} />
        </ConfigurationWrapper>
    );
});
